import type { Framing, ViewEndpoint } from "../protocol/events";
import { log } from "../shared/debug";
import {
  invertHomography,
  overscanArea,
  sameRect,
  sameWindow,
  windowHomography,
  type SourceWindow,
} from "../shared/geometry";
import type { ViewControl } from "./view-control";

/**
 * The view's half of the framing handshake, without any drawing.
 *
 * The scanner scans whatever window the view sends as its `framing`, and the video has
 * to be placed on screen by that same window's inverse — or what the user sees and what
 * the detector sees drift apart. Both come from one computation, kept here so a design
 * can call it from wherever its camera area lives: a {@link Framer} turns the
 * ViewControl's state into a window for a camera area of a given size and sends it to
 * the scanner when it changes; {@link videoStyle} is the CSS that puts the video under
 * that area; {@link projectQuad} lifts a quad out of a `frame` onto a raster covering
 * it.
 *
 * "Camera area" throughout means the on-screen rectangle the camera fills, in CSS
 * pixels. The reference view uses the whole viewport; a design may reserve a strip for
 * its controls and hand in the smaller rectangle — whatever it hands in is exactly what
 * is scanned.
 */

/** The raster the camera delivers, from `session:started` and `sensor:resized`. */
export interface Sensor {
  width: number;
  height: number;
}

export class Framer {
  private sent: Framing | null = null;

  constructor(
    private readonly endpoint: ViewEndpoint,
    readonly control: ViewControl,
  ) {}

  /**
   * The window for a camera area of `cameraW` x `cameraH`, sent to the scanner if it
   * differs from the last one sent. Null while there is no sensor to frame.
   *
   * Call it whenever the window may have moved — a gesture, a resize, a slider, the
   * start of a session — and at least once in the `session:started` handler, because
   * the scanner's first frame waits on it.
   */
  frame(sensor: Sensor | null, cameraW: number, cameraH: number): SourceWindow | null {
    if (!sensor || !sensor.width || !sensor.height || cameraW <= 0 || cameraH <= 0) return null;
    const window = this.control.window(sensor.width, sensor.height, cameraW, cameraH);
    if (!window.sw || !window.sh) return null;
    const area = overscanArea(window, this.control.detectRect(window), sensor.width, sensor.height);
    if (!this.sent || !sameWindow(this.sent.window, window) || !sameRect(this.sent.area, area)) {
      this.sent = { window, area, crop: this.control.crop };
      // The scanner announces its raster once per session; log subsequent view changes
      // here, where the exact same window is sent and used to place the native video.
      log(
        "ui",
        `sensor ${sensor.width}x${sensor.height} → on-screen scan region ` +
          `${window.sw.toFixed(0)}x${window.sh.toFixed(0)}; ` +
          `map [${window.affine.map((value) => value.toFixed(3)).join(", ")}]; ` +
          `desk ${window.keystone.toFixed(3)}; detection ` +
          `${area.w.toFixed(0)}x${area.h.toFixed(0)} at ${area.x.toFixed(0)},${area.y.toFixed(0)}`,
      );
      this.endpoint.emit("framing", this.sent);
    }
    return window;
  }

  /** Forget what was sent: the next `frame` sends again. For `session:stopped`. */
  reset(): void {
    this.sent = null;
  }
}

/**
 * Where the `<video>` goes: sized to the sensor raster and mapped onto the camera area
 * by the window's inverse. CSS `matrix3d` does the same projective divide as the GL
 * sampler, which keeps panning, rotation and desk view visually exact without a
 * per-frame canvas copy. Apply with `Object.assign(video.style, videoStyle(…))`; the
 * element needs `position: absolute; top: 0; left: 0; transform-origin: 0 0` and no
 * max-width, and its container clips at the camera area's edges.
 */
export function videoStyle(
  window: SourceWindow,
  sensor: Sensor,
  cameraW: number,
  cameraH: number,
): { width: string; height: string; transform: string } {
  const toView = invertHomography(windowHomography(window));
  const sx = cameraW / window.sw;
  const sy = cameraH / window.keystoneSpan;
  const d = [
    sx * toView[0],
    sx * toView[1],
    sx * toView[2],
    sy * toView[3],
    sy * toView[4],
    sy * toView[5],
    toView[6],
    toView[7],
    toView[8],
  ];
  return {
    width: `${sensor.width}px`,
    height: `${sensor.height}px`,
    transform:
      `matrix3d(${d[0]},${d[3]},0,${d[6]},` +
      `${d[1]},${d[4]},0,${d[7]},0,0,1,0,` +
      `${d[2]},${d[5]},0,${d[8]})`,
  };
}

/**
 * A quad in window pixels — as every `frame` carries them — onto a raster of
 * `targetW` x `targetH` that covers the camera area. One scale per axis: window space
 * is aligned with the screen whatever the rotation, and the video has already applied
 * the inverse projection, desk view included.
 */
export function projectQuad(
  quad: Float32Array,
  window: SourceWindow,
  targetW: number,
  targetH: number,
): number[] {
  const toX = targetW / window.sw;
  const toY = targetH / window.keystoneSpan;
  const pts = new Array<number>(8);
  for (let i = 0; i < 8; i += 2) {
    pts[i] = quad[i] * toX;
    pts[i + 1] = quad[i + 1] * toY;
  }
  return pts;
}
